import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, MotionConfig, useReducedMotion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { ThemeContext } from '../ThemeContext';
import { WindowManagerContext } from './WindowManagerContext';
import { ProjectsIconMobile, ContactIconMobile } from './IconSet';
import { getCaseStudy } from '../content/caseStudies';
import WaveBackground from './WaveBackground';

const appDetails = {
  about: { label: 'About me', color: '#ffdfbc' },
  projects: { label: 'Projects', color: '#c5dcff' },
  experience: { label: 'Experience', color: '#ddd3fa' },
  contact: { label: 'Contact', color: '#cdebdc' },
  settings: { label: 'Settings', color: '#e0e5ed' },
  chat: { label: 'Local chat', color: '#f6d5e1' },
  'work-with-me': { label: 'Work with me', color: '#ffe5ac' },
};

const Shell = styled.div`
  --mobile-text: ${p => p.$theme === 'light' ? '#223044' : '#f1f3f7'};
  --mobile-muted: ${p => p.$theme === 'light' ? '#586579' : '#b9c3d2'};
  --mobile-surface: ${p => p.$theme === 'light' ? '#fcfcfa' : '#202a39'};
  --mobile-border: ${p => p.$theme === 'light' ? '#dfe5ec' : '#3b4759'};
  --mobile-app: ${p => p.$theme === 'light' ? '#fff' : '#1e1e1e'};
  position: relative;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--mobile-text);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const Header = styled.header`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 64px;
  padding: max(8px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) 8px max(16px, env(safe-area-inset-left));
  background: ${p => p.$home ? 'transparent' : 'var(--mobile-app)'};
  border-bottom: 1px solid ${p => p.$home ? 'transparent' : 'var(--mobile-border)'};
`;

const HeaderTitle = styled.h1`
  flex: 1;
  min-width: 0;
  font-size: 0.95rem;
  line-height: 1.3;
  font-weight: 650;
  letter-spacing: -0.02em;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const IconButton = styled.button`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid var(--mobile-border);
  border-radius: 50%;
  background: var(--mobile-surface);
  color: var(--mobile-text);
  cursor: pointer;
  &:hover { background: var(--mobile-surface); border-color: var(--mobile-text); }
`;

const BackButton = styled(IconButton)`
  width: auto;
  gap: 4px;
  padding: 0 10px 0 2px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  font-size: 0.9rem;
  &:hover { background: var(--mobile-surface); }
`;

const HomeScreen = styled.main`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior-y: contain;
  padding: 8px max(20px, env(safe-area-inset-right)) calc(112px + env(safe-area-inset-bottom)) max(20px, env(safe-area-inset-left));
  &[hidden] { display: none; }
`;

const HomeLayout = styled.div`
  max-width: 440px;
  margin: 0 auto;
`;

const IntroWidget = styled.section`
  padding: 22px;
  border-radius: 24px;
  background: var(--mobile-surface);
  box-shadow: 0 4px 14px rgba(15, 39, 70, 0.06);
  position: relative;

  img {
    position: absolute;
    top: 22px;
    right: 22px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    object-fit: cover;
  }
  h2 { font-size: 2.4rem; line-height: 1.1; letter-spacing: -0.05em; margin: 7px 65px 12px 0; }
  > p:last-child { font-size: 1rem; line-height: 1.55; max-width: 28em; }
`;

const Eyebrow = styled.p`
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  font-weight: 650;
  color: var(--mobile-muted);
`;

const FeaturedProject = styled.button`
  display: block;
  position: relative;
  width: 100%;
  margin-top: 14px;
  padding: 20px 22px;
  text-align: left;
  border: 0;
  border-radius: 24px;
  background: ${p => p.$theme === 'light' ? '#ece8fa' : '#36314e'};
  color: var(--mobile-text);
  font: inherit;
  cursor: pointer;

  &:hover { background: ${p => p.$theme === 'light' ? '#e3ddf6' : '#40395c'}; }
  strong { display: block; max-width: 15em; margin: 9px 30px 7px 0; font-size: 1.13rem; line-height: 1.25; letter-spacing: -0.025em; }
  .summary { display: block; color: var(--mobile-muted); font-size: 0.85rem; line-height: 1.5; }
  .read-story { display: flex; align-items: center; gap: 6px; margin-top: 12px; font-size: 0.8rem; font-weight: 650; }
  .project-icon { position: absolute; right: 22px; top: 20px; color: var(--mobile-muted); }
`;

const AppGrid = styled.nav`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px 12px;
  margin-top: 28px;
`;

const AppButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--mobile-text);
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: transparent; }
  &:active > span:first-child { transform: scale(0.95); }
  > span:last-child { text-shadow: 0 1px 3px var(--mobile-surface); }
`;

const AppIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  border-radius: 18px;
  background: ${p => p.$color};
  color: #293b55;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.55), 0 3px 8px rgba(13,36,68,0.09);
  svg { width: 29px; height: 29px; stroke-width: 1.7; }
`;

const AppScreen = styled(motion.main)`
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  background: var(--mobile-app);
  padding: 16px max(10px, env(safe-area-inset-right)) calc(112px + env(safe-area-inset-bottom)) max(10px, env(safe-area-inset-left));
  overflow-wrap: anywhere;
  &[hidden] { display: none; }
  /* Let long case studies use this single scroll area. */
  > div { height: auto; }
`;

const Dock = styled.nav`
  position: absolute;
  z-index: 20;
  bottom: max(14px, env(safe-area-inset-bottom));
  left: 50%;
  transform: translateX(-50%);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: min(320px, calc(100% - 40px));
  padding: 7px;
  border: 1px solid var(--mobile-border);
  border-radius: 26px;
  background: var(--mobile-surface);
  box-shadow: 0 8px 30px rgba(9, 26, 48, 0.18);
`;

const DockButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 56px;
  border: 0;
  border-radius: 19px;
  background: transparent;
  color: var(--mobile-muted);
  font: inherit;
  font-size: 0.65rem;
  font-weight: 600;
  cursor: pointer;
  &[aria-current='page'] { color: var(--mobile-text); background: ${p => p.$theme === 'light' ? '#e5edf7' : '#35445a'}; }
  &:hover { color: var(--mobile-text); background: ${p => p.$theme === 'light' ? '#e5edf7' : '#35445a'}; }
  svg { width: 22px; height: 22px; }
`;

const Glyph = ({ name }) => (
  <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {name === 'home' && <><path d="m3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z" /></>}
    {name === 'back' && <path d="m14 6-6 6 6 6" />}
    {name === 'arrow' && <path d="M5 12h14m-6-6 6 6-6 6" />}
    {name === 'moon' && <path d="M20 15a8 8 0 0 1-11-11A9 9 0 1 0 20 15Z" />}
    {name === 'sun' && <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" /></>}
  </svg>
);

export default function MobileInterface({ sections, renderSection }) {
  const { theme, setTheme } = useContext(ThemeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const reduceMotion = useReducedMotion();
  const titleRef = useRef(null);
  const previousApp = useRef(null);
  const requestedApp = new URLSearchParams(location.search).get('app') || 'home';
  const caseStudy = requestedApp.startsWith('case-study:') && getCaseStudy(requestedApp.slice(11));
  const activeApp = sections.some(s => s.id === requestedApp) || caseStudy ? requestedApp : 'home';
  const [visited, setVisited] = useState([]);
  const isHome = activeApp === 'home';
  const title = isHome ? "Xander's space" : caseStudy ? 'Project story' : appDetails[activeApp].label;

  useEffect(() => {
    if (activeApp !== 'home') {
      setVisited(previous => previous.includes(activeApp) ? previous : [...previous, activeApp]);
    }
    if (previousApp.current !== null && previousApp.current !== activeApp) {
      titleRef.current?.focus({ preventScroll: true });
    }
    previousApp.current = activeApp;
  }, [activeApp]);

  const openApp = (id, source = 'mobile_app') => {
    const allowed = id === 'home' || sections.some(s => s.id === id)
      || (id.startsWith('case-study:') && getCaseStudy(id.slice(11)));
    if (!allowed || id === activeApp) return;
    const params = new URLSearchParams(location.search);
    if (id === 'home') params.delete('app');
    else params.set('app', id);
    posthog?.capture('window_opened', { window: id, source });
    navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, {
      state: { ...location.state, mobileDepth: (location.state?.mobileDepth || 0) + 1 },
    });
  };

  const goBack = () => {
    if (location.state?.mobileDepth > 0) navigate(-1);
    else {
      const params = new URLSearchParams(location.search);
      params.delete('app');
      navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, { replace: true });
    }
  };

  const screens = [...new Set([...visited, activeApp])].filter(id => id !== 'home'
    && (sections.some(s => s.id === id) || (id.startsWith('case-study:') && getCaseStudy(id.slice(11)))));

  return (
    <WindowManagerContext.Provider value={{ openWindow: openApp, openCaseStudy: (slug, source) => openApp(`case-study:${slug}`, source), openPalette: () => {} }}>
      <MotionConfig reducedMotion="user">
        <Shell $theme={theme}>
          <WaveBackground />
          <Header $home={isHome}>
            {!isHome && <BackButton type="button" onClick={goBack}><Glyph name="back" />Back</BackButton>}
            <HeaderTitle ref={titleRef} tabIndex={-1}>{title}</HeaderTitle>
            <IconButton type="button" aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`} onClick={() => {
              const next = theme === 'light' ? 'dark' : 'light';
              posthog?.capture('theme_toggled', { theme: next });
              setTheme(next);
            }}><Glyph name={theme === 'light' ? 'moon' : 'sun'} /></IconButton>
          </Header>

          <HomeScreen hidden={!isHome} aria-label="Home screen">
            <HomeLayout>
              <IntroWidget>
                <Eyebrow>Hello, I'm</Eyebrow>
                <img src="/images/portrait.webp" alt="" width="56" height="56" />
                <h2>Xander.</h2>
                <p>A product manager who builds the tools he wishes existed.</p>
              </IntroWidget>
              <FeaturedProject $theme={theme} type="button" onClick={() => openApp('case-study:duration-tasks', 'mobile_featured_project')} aria-label="Read the story: Power Tools for Google Tasks">
                <Eyebrow as="span">From my workbench</Eyebrow>
                <span className="project-icon" aria-hidden="true"><ProjectsIconMobile /></span>
                <strong>Power Tools for Google Tasks</strong>
                <span className="summary">A missing feature became a side project.<br />Here's the story behind it.</span>
                <span className="read-story">Take a look <Glyph name="arrow" /></span>
              </FeaturedProject>
              <AppGrid aria-label="Apps">
                {sections.map(section => {
                  const { label, color } = appDetails[section.id];
                  const Icon = section.mobileIcon;
                  return <AppButton key={section.id} type="button" onClick={() => openApp(section.id, 'mobile_grid')}>
                    <AppIcon $color={color} aria-hidden="true"><Icon /></AppIcon>
                    <span>{label}</span>
                  </AppButton>;
                })}
              </AppGrid>
            </HomeLayout>
          </HomeScreen>

          {screens.map(id => <AppScreen key={id} hidden={id !== activeApp} aria-label={id.startsWith('case-study:') ? getCaseStudy(id.slice(11)).title : appDetails[id].label}
            initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduceMotion ? 0 : 0.18 }}>
            {renderSection(id)}
          </AppScreen>)}

          <Dock aria-label="Main navigation">
            <DockButton $theme={theme} type="button" aria-current={isHome ? 'page' : undefined} onClick={() => openApp('home', 'mobile_dock')}><Glyph name="home" />Home</DockButton>
            <DockButton $theme={theme} type="button" aria-current={activeApp === 'projects' || caseStudy ? 'page' : undefined} onClick={() => openApp('projects', 'mobile_dock')}><span aria-hidden="true"><ProjectsIconMobile /></span>Projects</DockButton>
            <DockButton $theme={theme} type="button" aria-current={activeApp === 'contact' ? 'page' : undefined} onClick={() => openApp('contact', 'mobile_dock')}><span aria-hidden="true"><ContactIconMobile /></span>Contact</DockButton>
          </Dock>
        </Shell>
      </MotionConfig>
    </WindowManagerContext.Provider>
  );
}
